import React from 'react';
import { Box, Grid2 } from '@mui/material';
import Wc from '@mui/icons-material/Wc';
import Event from '@mui/icons-material/Event';
import EditNote from '@mui/icons-material/EditNote';
import ChairAlt from '@mui/icons-material/ChairAlt';
import Place from '@mui/icons-material/Place';
import AssignmentInd from '@mui/icons-material/AssignmentInd';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import { useShowContext } from 'framework/show';
import { useAuthorization } from 'framework/authorization';
import moment from 'moment';
import {
  READ_EMPLOYEE_MAIN_PERSONAL_INFO,
  UPDATE_EMPLOYEE_MAIN_PERSONAL_INFO,
  EmployeeTypeDropdown,
  EMPLOYEE_TYPES_ALIAS,
  EMPLOYEE_WORKPLACE_LOCATION,
  useEmployeeService,
} from 'domain/shared/employee';
import { ShowSection } from 'framework/ui/layout';
import { Link } from 'react-router-dom';
import {
  useFormShowContext,
  DateInput,
  Switch,
  TextInput,
  GenderDropdown,
  FormFieldsData,
  useFormContext,
} from 'framework/form';
import { DATE_FORMAT } from 'framework/utils';
import FormActionButtons from '../../../shared/button/showEdit/FormActionButtons';
import EditFormShowButton from '../../../shared/button/showEdit/EditFormShowButton';
import EmployeeWorkplaceDropdown from 'domain/people/employee/shared/input/EmployeeWorkplaceDropdown';
import { DocumentScanUploaderInput, EmployeePhotoUploaderInput } from 'domain/shared/employee';
import EmployeePhotoDisplay from './EmployeePhotoDisplay';
import PersonalInfoItem from './PersonalInfoItem';
import { Badge } from 'framework/ui/mui-builder';
import { BadgeLayoutProps } from 'framework/ui/uiContract';

const formatToFirstUpperCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

const BIRTHDAY_CONSENT_CHIP = new Map<boolean, { label: string; color: BadgeLayoutProps['color'] }>([
  [true, { label: 'OK to greet', color: 'success' }],
  [false, { label: 'No greetings', color: 'default' }],
]);

const PersonalInfoContent: React.FC = () => {
  const { data: employee } = useShowContext();
  const { isGranted } = useAuthorization();
  const { getFullNameWithMiddleName, isWorkplaceLocationNotRemote } = useEmployeeService();
  const { isEditMode } = useFormShowContext();
  const { data: formContext } = useFormContext();
  const isGrantedUpdateMainInfo = isGranted(UPDATE_EMPLOYEE_MAIN_PERSONAL_INFO);

  if (!isGranted(READ_EMPLOYEE_MAIN_PERSONAL_INFO)) {
    return null;
  }

  const localName = employee?.getValueBySource('nameLocal');
  const latinName = employee?.getValueBySource('nameEn');
  const workingEmail = employee?.getValueBySource('workingEmail');
  const gender = employee?.getValueBySource('gender');
  const dateOfBirth = employee?.getValueBySource('extendedInformation.dateOfBirth');
  const employeeType = employee?.getValueBySource('employeeType');
  const birthdayCongratsConsence = !!employee?.getValueBySource('extendedInformation.birthdayCongratsConsent');
  const workplaceLocation = employee?.getValueBySource('extendedInformation.hrInformation.workplaceLocation');
  const roomNumber = employee?.getValueBySource('extendedInformation.hrInformation.roomNumber');
  const deskLocation = employee?.getValueBySource('extendedInformation.hrInformation.deskLocation');
  const requisites = employee?.getValueBySource('extendedInformation.requisitesInformation.requisites');
  const linkedCandidateId = employee?.getValueBySource('linkedCandidateId');
  const birthdayChipConfig = BIRTHDAY_CONSENT_CHIP.get(birthdayCongratsConsence)!;
  const isVisibleOfficeLocation = (data: FormFieldsData) =>
    isWorkplaceLocationNotRemote(data?.extendedInformation?.hrInformation?.workplaceLocation);

  const formatDate = (date: string) => {
    if (!date) return '';
    try {
      return moment(date).format(DATE_FORMAT.SIMPLE);
    } catch {
      return date;
    }
  };

  const getLocalNameDisplay = () => {
    if (!localName) return '';
    if (typeof localName === 'object') {
      return getFullNameWithMiddleName(localName);
    }
    return localName;
  };

  const getLatinNameDisplay = () => {
    if (!latinName) return '';
    if (typeof latinName === 'object') {
      const parts = [];
      if (latinName.firstName) parts.push(latinName.firstName);
      if (latinName.lastName) parts.push(latinName.lastName);
      return parts.length > 0 ? parts.join(' ') : '';
    }
    return latinName;
  };

  const getWorkplaceDisplay = () => {
    if (!workplaceLocation) return '';
    const roomNumberString = roomNumber ? `| ${roomNumber}` : '';
    const deskLocationString = deskLocation ? `| ${deskLocation}` : '';

    return `${EMPLOYEE_WORKPLACE_LOCATION[workplaceLocation] || workplaceLocation} ${roomNumberString} ${deskLocationString}`;
  };

  const getEditActions = () => {
    return (
      <Box
        sx={{
          position: 'absolute',
          right: '-18px',
          top: '-19px',
        }}
      >
        {isEditMode ? (
          <FormActionButtons />
        ) : (
          <EditFormShowButton requiredPermission={UPDATE_EMPLOYEE_MAIN_PERSONAL_INFO} />
        )}
      </Box>
    );
  };

  if (isEditMode) {
    return (
      <ShowSection
        sx={{
          position: 'relative',
        }}
        actions={getEditActions()}
        disableDefaultPadding
      >
        <Box mt="22px">
          <Grid2 container>
            {isGrantedUpdateMainInfo && (
              <Grid2 size={12}>
                <Box>
                  <Grid2 container spacing={2}>
                    <Grid2 size={4}>
                      <TextInput source="nameEn.firstName" label="First latin name" isRequired />
                    </Grid2>
                    <Grid2 size={4}>
                      <TextInput source="nameEn.lastName" label="Last latin name" isRequired />
                    </Grid2>
                    <Grid2 size={4}>
                      <TextInput source="workingEmail" label="Syberry email" isRequired />
                    </Grid2>
                  </Grid2>
                </Box>
              </Grid2>
            )}
          </Grid2>
          <Grid2 container>
            <Grid2 size={12}>
              <Box>
                <Grid2 container spacing={2}>
                  <Grid2 size={4}>
                    <TextInput source="nameLocal.firstName" label="First local name" isRequired />
                  </Grid2>
                  <Grid2 size={4}>
                    <TextInput source="nameLocal.lastName" label="Last local name" isRequired />
                  </Grid2>
                  <Grid2 size={4}>
                    <TextInput source="nameLocal.middleName" label="Middle local name" />
                  </Grid2>
                </Grid2>
              </Box>

              <Box>
                <Grid2 container spacing={2}>
                  <Grid2 size={4}>
                    <GenderDropdown source="gender" label="Gender" isRequired />
                  </Grid2>
                  <Grid2 size={4}>
                    <DateInput source="extendedInformation.dateOfBirth" label="Date of birth" />
                  </Grid2>
                  <Grid2 size={4}>
                    <Switch
                      sx={{ mt: 2 }}
                      source="extendedInformation.birthdayCongratsConsent"
                      label="Birthday greetings consent"
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <EmployeeTypeDropdown source="employeeType" label="Worker type" isRequired />
                  </Grid2>
                  <Grid2 size={4}>
                    <EmployeeWorkplaceDropdown
                      source="extendedInformation.hrInformation.workplaceLocation"
                      label="Workplace location"
                      isRequired
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <TextInput
                      source="extendedInformation.hrInformation.roomNumber"
                      label="Room number"
                      isVisible={isVisibleOfficeLocation}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <TextInput
                      source="extendedInformation.hrInformation.deskLocation"
                      label="Desk location"
                      isVisible={isVisibleOfficeLocation}
                    />
                  </Grid2>
                </Grid2>
              </Box>
              {formContext?.employeeType === EMPLOYEE_TYPES_ALIAS.CONTRACTOR && !requisites && (
                <Box>
                  <Grid2 container spacing={2}>
                    <Grid2 size={4}>
                      <TextInput source="extendedInformation.requisitesInformation.requisites" label="Requisites" />
                      <DocumentScanUploaderInput
                        source="extendedInformation.requisitesInformation.scansIds"
                        label="Document scans"
                      />
                    </Grid2>
                  </Grid2>
                </Box>
              )}
              <Box sx={{ marginBottom: 3 }}>
                <EmployeePhotoUploaderInput label="Worker photos" source="extendedInformation.photosIds" isRequired />
              </Box>
            </Grid2>
          </Grid2>
        </Box>
      </ShowSection>
    );
  }

  return (
    <ShowSection
      sx={{
        position: 'relative',
      }}
      actions={getEditActions()}
      disableDefaultPadding
    >
      <Grid2 container alignItems="center" justifyContent="start" gap={6} maxWidth="1200px">
        {employee?.getValueBySource('extendedInformation.photosIds')?.length > 0 && (
          <Grid2
            sx={{
              gridRow: 'span 2',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              paddingTop: 0.5,
              paddingLeft: 1,
            }}
          >
            <EmployeePhotoDisplay photosIds={employee?.getValueBySource('extendedInformation.photosIds')} />
          </Grid2>
        )}

        <Grid2 display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} alignItems="stretch" size={9}>
          {latinName && (
            <PersonalInfoItem icon={<AssignmentInd />} value={getLatinNameDisplay()} tooltip="Latin name" />
          )}
          {localName && <PersonalInfoItem icon={<Place />} value={getLocalNameDisplay()} tooltip="Localized name" />}
          {gender && <PersonalInfoItem icon={<Wc />} value={formatToFirstUpperCase(gender)} />}
          {dateOfBirth && (
            <Box display="flex" gap={2} alignItems="center">
              <PersonalInfoItem icon={<Event />} value={formatDate(dateOfBirth)} />
              <Badge
                color={birthdayChipConfig.color}
                sx={{
                  p: 0.5,
                }}
              >
                {birthdayChipConfig.label}
              </Badge>
            </Box>
          )}
          {workingEmail && <PersonalInfoItem icon={<EmailOutlined />} value={workingEmail} tooltip="Syberry email" />}
          {workplaceLocation && <PersonalInfoItem icon={<ChairAlt />} value={getWorkplaceDisplay()} />}
          {employeeType && <PersonalInfoItem icon={<EditNote />} value={formatToFirstUpperCase(employeeType)} />}
          {linkedCandidateId && (
            <Box display="flex" alignItems="center">
              <Link to={`/candidates/${linkedCandidateId}`}>Linked Candidate</Link>
            </Box>
          )}
        </Grid2>
      </Grid2>
    </ShowSection>
  );
};

export default PersonalInfoContent;
